/**
 * Kode AI's use of Gemini, as the dashboard shows it
 * (components/kode_ai/Kode_AI_Usage): the API's reading (its
 * utils/kode_ai/usage) turned into meters. For each model Kode tries:
 * requests today, requests and input tokens in the last minute, and input
 * tokens today where Google sets a daily token limit, each against its
 * limit, filling up as the limit nears.
 *
 * Google's free tier sets those limits per model and per project. Requests
 * per day reset at midnight Pacific time; the per-minute ones run over the
 * last 60 seconds, so those meters drain as the seconds pass, between
 * readings too: the reading carries the last minute's calls one by one.
 */

export type KodeAiLimitKind = 'requestsPerMinute' | 'inputTokensPerMinute' | 'requestsPerDay' | 'inputTokensPerDay';

export type KodeAiOutcome = 'answered' | 'refused' | 'failed';

export interface KodeAiLimitReading {
  value: number | null;
  source: 'set' | 'google' | null;
  google: number | null;
  googleAt: string | null;
}

export interface KodeAiRefusal {
  kind: KodeAiLimitKind | null;
  quotaId: string | null;
  value: number | null;
  at: string;
  retryAfterSeconds: number | null;
}

export interface KodeAiModelReading {
  model: string;
  choice: number | null;
  today: {
    requests: number;
    answered: number;
    refused: number;
    failed: number;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    lastAt: string | null;
  };
  limits: Record<KodeAiLimitKind, KodeAiLimitReading>;
  refusals: KodeAiRefusal[];
}

export interface KodeAiUsageSnapshot {
  now: string;
  zone: string;
  dayStartedAt: string;
  resetsAt: string;
  configured: boolean;
  chain: string[];
  totals: {
    questions: number;
    questionsAnswered: number;
    requests: number;
    answered: number;
    refused: number;
    failed: number;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
  models: KodeAiModelReading[];
  recent: Array<{ at: string; model: string; outcome: KodeAiOutcome; inputTokens: number }>;
  recentWindowSeconds: number;
}


/** How often the dashboard asks for a new reading. */
export const KODE_AI_POLL_MS = 5 * 1000;

/** Where AI Studio shows the project's limits, model by model. */
export const KODE_AI_STUDIO_RATE_LIMITS_URL = 'https://aistudio.google.com/rate-limit';

/** A meter turns amber from this share of its limit, and red from the next. */
export const KODE_AI_WARN_PERCENT = 70;
export const KODE_AI_DANGER_PERCENT = 90;

const MINUTE_MS = 60 * 1000;

export interface KodeAiLimitDefinition {
  kind: KodeAiLimitKind;
  /** The meter's name. */
  label: string;
  /** The field's name in the limits form, and AI Studio's abbreviation. */
  field: string;
  abbr: string;
  window: 'day' | 'minute';
  unit: 'requests' | 'tokens';
  /** Shown only where a limit is known: Google sets it for some models only. */
  optional?: boolean;
}

export const KODE_AI_LIMITS: KodeAiLimitDefinition[] = [
  { kind: 'requestsPerDay', label: 'Requests today', field: 'Requests per day', abbr: 'RPD', window: 'day', unit: 'requests' },
  { kind: 'requestsPerMinute', label: 'Requests per minute', field: 'Requests per minute', abbr: 'RPM', window: 'minute', unit: 'requests' },
  { kind: 'inputTokensPerMinute', label: 'Input tokens per minute', field: 'Input tokens per minute', abbr: 'TPM', window: 'minute', unit: 'tokens' },
  { kind: 'inputTokensPerDay', label: 'Input tokens today', field: 'Input tokens per day', abbr: 'TPD', window: 'day', unit: 'tokens', optional: true },
];

export type KodeAiMeterTone = 'ok' | 'warning' | 'danger' | 'unknown';

export interface KodeAiMeter {
  kind: KodeAiLimitKind;
  label: string;
  abbr: string;
  window: 'day' | 'minute';
  unit: 'requests' | 'tokens';
  used: number;
  limit: number | null;
  limitSource: 'set' | 'google' | null;
  /** Of the limit, 0–100; 100 once reached. */
  percent: number;
  tone: KodeAiMeterTone;
  /** At or past the limit, or Google is refusing calls for it. */
  reached: boolean;
  /** Google's refusal that still stands for this limit, if any. */
  refusal: KodeAiRefusal | null;
}

/** The model's calls in the 60 seconds before `nowMs`: how many (ones Google refused don't count) and their input tokens. */
export function lastMinuteOf(snapshot: KodeAiUsageSnapshot, model: string, nowMs: number): { requests: number; inputTokens: number } {
  const since = nowMs - MINUTE_MS;
  let requests = 0;
  let inputTokens = 0;
  snapshot.recent.forEach((call) => {
    if (call.model !== model || call.outcome === 'refused') return;
    const at = Date.parse(call.at);
    if (!Number.isFinite(at) || at <= since) return;
    requests += 1;
    inputTokens += Number(call.inputTokens) || 0;
  });
  return { requests, inputTokens };
}

/**
 * Whether a refusal from Google still holds a limit: a day's until the day
 * resets, a minute's for as long as Google said to wait (a minute if it
 * didn't say).
 */
function standingRefusal(reading: KodeAiModelReading, kind: KodeAiLimitKind, nowMs: number, dayStartMs: number): KodeAiRefusal | null {
  const window = KODE_AI_LIMITS.find((definition) => definition.kind === kind)?.window;
  let standing: KodeAiRefusal | null = null;
  reading.refusals.forEach((refusal) => {
    if (refusal.kind !== kind) return;
    const at = Date.parse(refusal.at);
    if (!Number.isFinite(at)) return;
    const holds = window === 'day'
      ? at >= dayStartMs
      : nowMs - at < Math.max(MINUTE_MS, (refusal.retryAfterSeconds || 0) * 1000);
    if (holds && (!standing || at > Date.parse(standing.at))) standing = refusal;
  });
  return standing;
}

/** A refusal Google gave no limit for, in the last ten minutes: shown on the model, not on a meter. */
export function unexplainedRefusal(reading: KodeAiModelReading, nowMs: number): KodeAiRefusal | null {
  const recent = reading.refusals
    .filter((refusal) => !refusal.kind && nowMs - Date.parse(refusal.at) < 10 * MINUTE_MS)
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  return recent[0] || null;
}

/** The model's meters as of `nowMs` (the server's clock). */
export function metersOf(snapshot: KodeAiUsageSnapshot, reading: KodeAiModelReading, nowMs: number): KodeAiMeter[] {
  const minute = lastMinuteOf(snapshot, reading.model, nowMs);
  const dayStartMs = Date.parse(snapshot.dayStartedAt);
  return KODE_AI_LIMITS
    .filter((definition) => !definition.optional || reading.limits[definition.kind]?.value !== null)
    .map((definition) => {
      const limitReading = reading.limits[definition.kind] || { value: null, source: null, google: null, googleAt: null };
      const used = definition.kind === 'requestsPerDay' ? reading.today.requests
        : definition.kind === 'inputTokensPerDay' ? reading.today.inputTokens
          : definition.kind === 'requestsPerMinute' ? minute.requests
            : minute.inputTokens;
      const limit = limitReading.value;
      const refusal = standingRefusal(reading, definition.kind, nowMs, dayStartMs);
      // A limit of 0 (no free allowance at all) counts as reached.
      const reached = Boolean(refusal) || (limit !== null && used >= limit);
      const percent = limit === null ? 0 : reached ? 100 : Math.min(100, (used / limit) * 100);
      const tone: KodeAiMeterTone = reached ? 'danger'
        : limit === null ? 'unknown'
          : percent >= KODE_AI_DANGER_PERCENT ? 'danger'
            : percent >= KODE_AI_WARN_PERCENT ? 'warning'
              : 'ok';
      return {
        kind: definition.kind,
        label: definition.label,
        abbr: definition.abbr,
        window: definition.window,
        unit: definition.unit,
        used,
        limit,
        limitSource: limitReading.source,
        percent,
        tone,
        reached,
        refusal,
      };
    });
}

/* -------------------------------- wording -------------------------------- */

export const formatCount = (value: number | null | undefined): string => (Number(value) || 0).toLocaleString('en-US');

/** "0%", "<0.1%", "3.7%", "62%". */
export function formatPercent(percent: number): string {
  if (!percent) return '0%';
  if (percent < 0.1) return '<0.1%';
  if (percent < 10) return `${(Math.floor(percent * 10) / 10).toFixed(1)}%`;
  return `${Math.floor(percent)}%`;
}

/** "6 h 12 min", "12 min", "45 s". */
export function formatDuration(ms: number): string {
  const seconds = Math.max(0, Math.ceil(ms / 1000));
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

/** "1st choice", "2nd choice"… */
export function choiceLabel(choice: number | null): string {
  if (!choice) return 'Not in use';
  const suffix = choice % 100 >= 11 && choice % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[choice % 10] || 'th';
  return `${choice}${suffix} choice`;
}

/** What a refusal was about, for a sentence: "the requests-per-day limit". */
export function limitPhrase(kind: KodeAiLimitKind | null): string {
  switch (kind) {
    case 'requestsPerDay': return 'the requests-per-day limit';
    case 'requestsPerMinute': return 'the requests-per-minute limit';
    case 'inputTokensPerMinute': return 'the input-tokens-per-minute limit';
    case 'inputTokensPerDay': return 'the input-tokens-per-day limit';
    default: return 'a limit';
  }
}
